import { useMutation, useQuery } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";
import {
  activateTierRules,
  deactivateTierRules,
} from "../graphql/mutations/tierRulesStatus";
import { updateTierRules } from "../graphql/mutations/updateTierRules";
import { loadTierRules } from "../graphql/queries/loadTierRules";

const LIFECYCLE_POLL_INTERVAL_MS = 2000;

const isTierOperationInProgressError = (error) => {
  const messages = [
    error?.message,
    ...(error?.graphQLErrors || []).map((graphQLError) => graphQLError.message),
  ].filter(Boolean);

  return messages.some(
    (message) =>
      message.includes("TIER_OPERATION_IN_PROGRESS") ||
      message.includes("tier lifecycle operation is in progress")
  );
};

export const useTierRules = () => {
  const { data, loading, error, refetch } = useQuery(loadTierRules, {
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const [executeUpdate, { loading: saving }] = useMutation(updateTierRules);
  const [executeActivate, { loading: activating }] = useMutation(activateTierRules);
  const [executeDeactivate, { loading: deactivating }] =
    useMutation(deactivateTierRules);
  const [lifecycleOperation, setLifecycleOperation] = useState(null);
  const [lifecycleCompletion, setLifecycleCompletion] = useState(null);

  const tierRules = useMemo(() => {
    return data?.loadTierRules || null;
  }, [data]);

  const lifecycleInProgress =
    Boolean(lifecycleOperation) || tierRules?.status === "PROCESSING";

  useEffect(() => {
    if (!lifecycleOperation) {
      return undefined;
    }

    let cancelled = false;
    const pollLifecycleOperation = async () => {
      try {
        const result = await refetch();
        const refreshedStatus = result?.data?.loadTierRules?.status;

        if (!cancelled && refreshedStatus && refreshedStatus !== "PROCESSING") {
          const succeeded =
            (lifecycleOperation.operationType === "ACTIVATE" &&
              refreshedStatus === "ACTIVE") ||
            (lifecycleOperation.operationType === "INACTIVATE" &&
              refreshedStatus === "INACTIVE");
          setLifecycleCompletion({
            operationId: lifecycleOperation.operationId,
            operationType: lifecycleOperation.operationType,
            succeeded,
          });
          setLifecycleOperation(null);
        }
      } catch {
        // Keep the UI locked and try again on the next poll. The user must not
        // start a second lifecycle operation while its final state is unknown.
      }
    };

    const interval = setInterval(pollLifecycleOperation, LIFECYCLE_POLL_INTERVAL_MS);
    pollLifecycleOperation();

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [lifecycleOperation, refetch]);

  const handleUpdateTierRules = async (input) => {
    const result = await executeUpdate({ variables: { input } });
    const response = result?.data?.updateTierRules;

    // Check if the response is a MetricChangeBlocked error
    if (response?.errorCode) {
      throw new Error(response.message || "Metric change is blocked");
    }

    await refetch();
    return result;
  };

  const handleTierRulesStatusOperation = async (
    executeOperation,
    operationName,
    tierRulesId
  ) => {
    let result;
    try {
      result = await executeOperation({ variables: { tierRulesId } });
    } catch (error) {
      if (isTierOperationInProgressError(error)) {
        throw new Error(
          "A tier activation or inactivation is already in progress. Wait for it to finish before trying again."
        );
      }
      throw error;
    }
    const response = result?.data?.[operationName];

    if (response?.errorCode) {
      throw new Error(
        response.errorSummary || "Tier structure status update failed"
      );
    }

    if (response?.outcome && response.outcome !== "COMPLETED") {
      throw new Error(
        response.errorSummary || "Tier structure operation did not complete"
      );
    }

    if (response?.operationId && response?.outcome == null) {
      setLifecycleCompletion(null);
      setLifecycleOperation(response);
    } else {
      await refetch();
    }
    return response;
  };

  const handleActivateTierRules = (tierRulesId) =>
    handleTierRulesStatusOperation(
      executeActivate,
      "activateTierRules",
      tierRulesId
    );

  const handleDeactivateTierRules = (tierRulesId) =>
    handleTierRulesStatusOperation(
      executeDeactivate,
      "deactivateTierRules",
      tierRulesId
    );

  return {
    tierRules,
    loading,
    error,
    saving: saving || activating || deactivating,
    lifecycleOperation,
    lifecycleCompletion,
    lifecycleInProgress,
    refetch,
    handleUpdateTierRules,
    handleActivateTierRules,
    handleDeactivateTierRules,
  };
};
